import {combine, createEvent, merge, sample} from 'effector';
import {createGate} from 'effector-react';

import {showRestError} from '@app/errors';
import {rootDomain} from '@app/model';
import {InnerFiltersElement, InnerSortingsElement} from '@shared/types/grid';
import {SelectOption} from '@shared/ui/Select';

import * as api from './api';
import {
    ContentTemplateSearchResponse,
    DictionaryItem,
    DictionarySearchResponse,
    MessageTemplateDto,
    MessageTemplateSearchResponse,
    NotificationTemplateDto,
    NotificationTemplateFilterNode,
    NotificationTemplateSearchResponse,
} from './types';

const domain = rootDomain.createDomain('DocumentTemplates');

export const DocumentTemplatesGate = createGate('DocumentTemplates');
export const DocumentTemplateDetailsGate = createGate<{notificationTemplateId: string}>(
    'DocumentTemplateDetails',
);
export const MessageTemplateDetailsGate = createGate<{
    notificationTemplateId: string;
    messageTemplateId: string;
}>('MessageTemplateDetails');

const fetchTemplatesFx = domain.createEffect({handler: api.fetchNotificationTemplates});
const fetchTemplateFx = domain.createEffect({handler: api.fetchNotificationTemplate});
const createTemplateFx = domain.createEffect({handler: api.createNotificationTemplate});
const saveTemplateFx = domain.createEffect({handler: api.updateNotificationTemplate});
const fetchMessageTemplatesFx = domain.createEffect({handler: api.fetchMessageTemplates});
const fetchMessageTemplateFx = domain.createEffect({handler: api.fetchMessageTemplate});
const saveMessageTemplateFx = domain.createEffect({handler: api.updateMessageTemplate});
const fetchContentTemplatesFx = domain.createEffect({handler: api.fetchContentTemplates});
const fetchViolationTypesFx = domain.createEffect({handler: api.fetchViolationTypes});
const fetchNotificationKindsFx = domain.createEffect({handler: api.fetchNotificationKinds});
const fetchActiveStatusesFx = domain.createEffect({handler: api.fetchActiveStatuses});
const fetchLanguagesFx = domain.createEffect({handler: api.fetchLanguages});
const fetchMessageChannelsFx = domain.createEffect({handler: api.fetchMessageChannels});
const fetchContentTypesFx = domain.createEffect({handler: api.fetchContentTypes});

[
    fetchTemplatesFx,
    fetchTemplateFx,
    createTemplateFx,
    saveTemplateFx,
    fetchMessageTemplatesFx,
    fetchMessageTemplateFx,
    saveMessageTemplateFx,
    fetchContentTemplatesFx,
    fetchViolationTypesFx,
    fetchNotificationKindsFx,
    fetchActiveStatusesFx,
    fetchLanguagesFx,
    fetchMessageChannelsFx,
    fetchContentTypesFx,
].forEach((fx) => fx.failData.watch(showRestError));

const data$ = domain
    .createStore<NotificationTemplateSearchResponse | null>(null)
    .on(fetchTemplatesFx.doneData, (_, data) => data)
    .reset(DocumentTemplatesGate.close);

const template$ = domain
    .createStore<NotificationTemplateDto | null>(null)
    .on(fetchTemplateFx.doneData, (_, data) => data)
    .on(createTemplateFx.doneData, (_, data) => data)
    .on(saveTemplateFx.doneData, (_, data) => data)
    .reset(DocumentTemplateDetailsGate.close, MessageTemplateDetailsGate.close);

const messageTemplates$ = domain
    .createStore<MessageTemplateSearchResponse | null>(null)
    .on(fetchMessageTemplatesFx.doneData, (_, data) => data)
    .reset(DocumentTemplateDetailsGate.close);

const messageTemplate$ = domain
    .createStore<MessageTemplateDto | null>(null)
    .on(fetchMessageTemplateFx.doneData, (_, data) => data)
    .on(saveMessageTemplateFx.doneData, (_, data) => data)
    .reset(MessageTemplateDetailsGate.close);

const contentTemplates$ = domain
    .createStore<ContentTemplateSearchResponse | null>(null)
    .on(fetchContentTemplatesFx.doneData, (_, data) => data)
    .reset(MessageTemplateDetailsGate.close);

const violationTypes$ = domain
    .createStore<DictionaryItem[]>([])
    .on(fetchViolationTypesFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(DocumentTemplatesGate.close);

const notificationKinds$ = domain
    .createStore<DictionaryItem[]>([])
    .on(fetchNotificationKindsFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(DocumentTemplatesGate.close);

const activeStatuses$ = domain
    .createStore<DictionaryItem[]>([])
    .on(fetchActiveStatusesFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(
        DocumentTemplatesGate.close,
        DocumentTemplateDetailsGate.close,
        MessageTemplateDetailsGate.close,
    );

const languages$ = domain
    .createStore<DictionaryItem[]>([])
    .on(fetchLanguagesFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(DocumentTemplateDetailsGate.close, MessageTemplateDetailsGate.close);

const messageChannels$ = domain
    .createStore<DictionaryItem[]>([])
    .on(fetchMessageChannelsFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(MessageTemplateDetailsGate.close);

const contentTypes$ = domain
    .createStore<DictionaryItem[]>([])
    .on(fetchContentTypesFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(MessageTemplateDetailsGate.close);

const violationTypeIds$ = domain.createStore<string[]>([]).reset(DocumentTemplatesGate.close);
const kindCode$ = domain.createStore<string | null>(null).reset(DocumentTemplatesGate.close);
const statusCode$ = domain.createStore<string | null>(null).reset(DocumentTemplatesGate.close);
const sortings$ = domain
    .createStore<Record<string, boolean | undefined>>({})
    .reset(DocumentTemplatesGate.close);
const pageNumber$ = domain.createStore(0).reset(DocumentTemplatesGate.close);
const pageSize$ = domain.createStore(10).reset(DocumentTemplatesGate.close);
const messagePageNumber$ = domain.createStore(0).reset(DocumentTemplateDetailsGate.close);
const messagePageSize$ = domain.createStore(10).reset(DocumentTemplateDetailsGate.close);
const contentPageNumber$ = domain.createStore(0).reset(MessageTemplateDetailsGate.close);
const contentPageSize$ = domain.createStore(10).reset(MessageTemplateDetailsGate.close);

export const violationTypeIdsChanged = createEvent<string[]>();
export const kindCodeChanged = createEvent<string | null>();
export const statusCodeChanged = createEvent<string | null>();
export const sortingsChanged = createEvent<InnerSortingsElement[]>();
export const pageNumberChanged = createEvent<number>();
export const pageSizeChanged = createEvent<number>();
export const messagePageNumberChanged = createEvent<number>();
export const messagePageSizeChanged = createEvent<number>();
export const contentPageNumberChanged = createEvent<number>();
export const contentPageSizeChanged = createEvent<number>();

violationTypeIds$.on(violationTypeIdsChanged, (_, value) => value);
kindCode$.on(kindCodeChanged, (_, value) => value);
statusCode$.on(statusCodeChanged, (_, value) => value);
sortings$.on(sortingsChanged, (state, sortings) => ({
    ...state,
    ...Object.fromEntries(sortings.map(({id, value}) => [id, value])),
}));
pageNumber$.on(pageNumberChanged, (_, value) => value);
pageSize$.on(pageSizeChanged, (_, value) => value);
pageNumber$.reset(violationTypeIdsChanged, kindCodeChanged, statusCodeChanged, pageSizeChanged);
messagePageNumber$.on(messagePageNumberChanged, (_, value) => value);
messagePageSize$.on(messagePageSizeChanged, (_, value) => value);
messagePageNumber$.reset(messagePageSizeChanged);
contentPageNumber$.on(contentPageNumberChanged, (_, value) => value);
contentPageSize$.on(contentPageSizeChanged, (_, value) => value);
contentPageNumber$.reset(contentPageSizeChanged);

const buildFilter = ({
    violationTypeIds,
    kindCode,
    statusCode,
}: {
    violationTypeIds: string[];
    kindCode: string | null;
    statusCode: string | null;
}): NotificationTemplateFilterNode | null => {
    const children: NotificationTemplateFilterNode[] = [];

    if (violationTypeIds.length) {
        children.push({type: 'IN', field: 'violationType', values: violationTypeIds});
    }

    if (kindCode) {
        children.push({type: 'EQ', field: 'kind.code', values: [kindCode]});
    }

    if (statusCode) {
        children.push({type: 'EQ', field: 'status.code', values: [statusCode]});
    }

    if (!children.length) return null;
    return children.length === 1 ? children[0] : {type: 'AND', children};
};

const mapSorts = (sortings: Record<string, boolean | undefined>) => {
    const [field, value] = Object.entries(sortings).find(([, sort]) => sort != null) ?? [];
    if (!field || value == null) return null;
    return [{field, order: value ? 'DESC' : 'ASC'} as const];
};

const toSelectedIds = (value: readonly SelectOption<string>[]) =>
    value.map((item) => item.value).filter((item): item is string => Boolean(item));

export const filtersChanged = createEvent<InnerFiltersElement[]>();
filtersChanged.watch((filters) => {
    const {id, value} = filters[0] ?? {};
    if (id === 'violationType') {
        violationTypeIdsChanged(toSelectedIds(value as readonly SelectOption<string>[]));
    }
    if (id === 'kind') {
        kindCodeChanged(
            ((value as SelectOption<string> | undefined)?.value ?? null) as string | null,
        );
    }
    if (id === 'status') {
        statusCodeChanged(
            ((value as SelectOption<string> | undefined)?.value ?? null) as string | null,
        );
    }
});

export const filtersReset = createEvent<string[]>();
filtersReset.watch((ids) => {
    const id = ids[0];
    if (id === 'violationType') violationTypeIdsChanged([]);
    if (id === 'kind') kindCodeChanged(null);
    if (id === 'status') statusCodeChanged(null);
});

sample({
    clock: DocumentTemplatesGate.open,
    target: [fetchViolationTypesFx, fetchNotificationKindsFx, fetchActiveStatusesFx],
});

sample({
    clock: DocumentTemplateDetailsGate.open,
    filter: ({notificationTemplateId}) =>
        Boolean(notificationTemplateId) && notificationTemplateId !== 'new',
    fn: ({notificationTemplateId}) => notificationTemplateId,
    target: fetchTemplateFx,
});

sample({
    clock: DocumentTemplateDetailsGate.open,
    target: [
        fetchViolationTypesFx,
        fetchNotificationKindsFx,
        fetchActiveStatusesFx,
        fetchLanguagesFx,
    ],
});

sample({
    clock: MessageTemplateDetailsGate.open,
    filter: ({notificationTemplateId}) => Boolean(notificationTemplateId),
    fn: ({notificationTemplateId}) => notificationTemplateId,
    target: fetchTemplateFx,
});

sample({
    clock: MessageTemplateDetailsGate.open,
    filter: ({messageTemplateId}) => Boolean(messageTemplateId) && messageTemplateId !== 'new',
    fn: ({messageTemplateId}) => messageTemplateId,
    target: fetchMessageTemplateFx,
});

sample({
    clock: MessageTemplateDetailsGate.open,
    target: [fetchActiveStatusesFx, fetchLanguagesFx, fetchMessageChannelsFx, fetchContentTypesFx],
});

sample({
    clock: [MessageTemplateDetailsGate.open, contentPageNumberChanged, contentPageSizeChanged],
    source: combine({
        gate: MessageTemplateDetailsGate.state,
        page: contentPageNumber$,
        size: contentPageSize$,
    }),
    filter: ({gate}) => Boolean(gate.messageTemplateId) && gate.messageTemplateId !== 'new',
    fn: ({gate, page, size}) => ({
        messageTemplateId: gate.messageTemplateId,
        page,
        size,
    }),
    target: fetchContentTemplatesFx,
});

sample({
    clock: [DocumentTemplateDetailsGate.open, messagePageNumberChanged, messagePageSizeChanged],
    source: combine({
        gate: DocumentTemplateDetailsGate.state,
        page: messagePageNumber$,
        size: messagePageSize$,
    }),
    filter: ({gate}) =>
        Boolean(gate.notificationTemplateId) && gate.notificationTemplateId !== 'new',
    fn: ({gate, page, size}) => ({
        notificationTemplateId: gate.notificationTemplateId,
        page,
        size,
    }),
    target: fetchMessageTemplatesFx,
});

sample({
    clock: merge([
        DocumentTemplatesGate.open,
        violationTypeIdsChanged,
        kindCodeChanged,
        statusCodeChanged,
        sortingsChanged,
        pageNumberChanged,
        pageSizeChanged,
    ]),
    source: combine({
        violationTypeIds: violationTypeIds$,
        kindCode: kindCode$,
        statusCode: statusCode$,
        sortings: sortings$,
        pageNumber: pageNumber$,
        pageSize: pageSize$,
    }),
    filter: DocumentTemplatesGate.status,
    fn: ({violationTypeIds, kindCode, statusCode, sortings, pageNumber, pageSize}) => ({
        filter: buildFilter({violationTypeIds, kindCode, statusCode}),
        sorts: mapSorts(sortings),
        page: {page: pageNumber, size: pageSize},
    }),
    target: fetchTemplatesFx,
});

const toIdOption = (item: DictionaryItem): SelectOption<string> => ({
    value: item.id,
    label: item.title?.en ?? Object.values(item.title ?? {})[0] ?? item.code,
});

const toCodeOption = (item: DictionaryItem): SelectOption<string> => ({
    value: item.code,
    label: item.title?.en ?? Object.values(item.title ?? {})[0] ?? item.code,
});

const filters$ = combine(
    {
        violationTypeIds: violationTypeIds$,
        kindCode: kindCode$,
        statusCode: statusCode$,
        violationTypes: violationTypes$,
        notificationKinds: notificationKinds$,
        activeStatuses: activeStatuses$,
    },
    ({
        violationTypeIds,
        kindCode,
        statusCode,
        violationTypes,
        notificationKinds,
        activeStatuses,
    }) => ({
        violationType: violationTypes
            .filter((item) => violationTypeIds.includes(item.id))
            .map(toIdOption),
        kind: kindCode
            ? toCodeOption(
                  notificationKinds.find((item) => item.code === kindCode) ??
                      ({code: kindCode} as DictionaryItem),
              )
            : undefined,
        status: statusCode
            ? toCodeOption(
                  activeStatuses.find((item) => item.code === statusCode) ??
                      ({code: statusCode} as DictionaryItem),
              )
            : undefined,
    }),
);

const pending$ = combine(
    [
        fetchTemplatesFx.pending,
        fetchViolationTypesFx.pending,
        fetchNotificationKindsFx.pending,
        fetchActiveStatusesFx.pending,
    ],
    (list) => list.some(Boolean),
);

const detailsPending$ = combine(
    [
        fetchTemplateFx.pending,
        createTemplateFx.pending,
        saveTemplateFx.pending,
        fetchMessageTemplatesFx.pending,
        fetchViolationTypesFx.pending,
        fetchNotificationKindsFx.pending,
        fetchActiveStatusesFx.pending,
        fetchLanguagesFx.pending,
    ],
    (list) => list.some(Boolean),
);

const messageDetailsPending$ = combine(
    [
        fetchTemplateFx.pending,
        fetchMessageTemplateFx.pending,
        saveMessageTemplateFx.pending,
        fetchContentTemplatesFx.pending,
        fetchActiveStatusesFx.pending,
        fetchLanguagesFx.pending,
        fetchMessageChannelsFx.pending,
        fetchContentTypesFx.pending,
    ],
    (list) => list.some(Boolean),
);

const $documentTemplatesState = combine({
    data: data$,
    violationTypes: violationTypes$,
    notificationKinds: notificationKinds$,
    activeStatuses: activeStatuses$,
    filters: filters$,
    sortings: sortings$,
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    pending: pending$,
});

const $documentTemplateDetailsState = combine({
    template: template$,
    messageTemplates: messageTemplates$,
    violationTypes: violationTypes$,
    notificationKinds: notificationKinds$,
    activeStatuses: activeStatuses$,
    languages: languages$,
    messagePageNumber: messagePageNumber$,
    messagePageSize: messagePageSize$,
    pending: detailsPending$,
});

const $messageTemplateDetailsState = combine({
    template: template$,
    messageTemplate: messageTemplate$,
    contentTemplates: contentTemplates$,
    activeStatuses: activeStatuses$,
    languages: languages$,
    messageChannels: messageChannels$,
    contentTypes: contentTypes$,
    contentPageNumber: contentPageNumber$,
    contentPageSize: contentPageSize$,
    pending: messageDetailsPending$,
});

export const documentTemplatesModel = {
    state: $documentTemplatesState,
    filtersChanged,
    filtersReset,
    sortingsChanged,
    pageNumberChanged,
    pageSizeChanged,
};

export const documentTemplateDetailsModel = {
    state: $documentTemplateDetailsState,
    createTemplate: createTemplateFx,
    saveTemplate: saveTemplateFx,
    messagePageNumberChanged,
    messagePageSizeChanged,
};

export const messageTemplateDetailsModel = {
    state: $messageTemplateDetailsState,
    saveMessageTemplate: saveMessageTemplateFx,
    contentPageNumberChanged,
    contentPageSizeChanged,
};
